/* istanbul ignore file */
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Label from 'reactstrap/lib/Label';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { FeatureSpecsEntities, VehicleEntities } from 'client/data/models/vehicle-v2';
import { getIfEvTaxCreditAmountPath, IncentiveModel } from 'client/data/models/incentives';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { INCENTIVE_TYPES } from 'site-modules/shared/constants/incentives';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { withTargetText } from 'site-modules/shared/utils/url-utils';
import { getStyleName, getDropdownItems } from 'site-modules/shared/utils/features-specs/utils';
import { isDiscontinuedDate } from 'site-modules/shared/utils/vehicle-utils';
import { getDealsUrl, getEvIncentivesUrl } from 'site-modules/shared/utils/core-link-constructor';
import { Link } from 'site-modules/shared/components/link/link';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { FancyLabel } from 'site-modules/shared/components/custom-dropdown-label/custom-dropdown-label';
import { LoadingDots } from 'site-modules/shared/components/loading-dots/loading-dots';

import './features-trim-box.plat-4078-chal3.scss';

function TaxRebateText({ taxRebateAmount }) {
  if (taxRebateAmount === null) {
    // null is the default value, meaning the api call was not yet made vs API would return 0 if there is no rebate
    return <LoadingDots />;
  }
  const taxRebateText = taxRebateAmount ? `Up to ${getPriceString(taxRebateAmount)}` : 'Not available';
  return <span>{taxRebateText}</span>;
}
TaxRebateText.propTypes = {
  taxRebateAmount: PropTypes.number,
};

TaxRebateText.defaultProps = {
  taxRebateAmount: null,
};

function FeaturesTrimBoxUI({ params, styleOverview, popularStyles, taxRebateAmount, className, onStyleChange }) {
  const [isDropdownRender, setDropdownRender] = useState(!params.styleId);
  const {
    styleAttributes: { electric, pluginElectric },
  } = styleOverview;

  const isElectric = electric || pluginElectric;

  useEffect(() => {
    setDropdownRender(true);
  }, []);

  const options = useMemo(() => getDropdownItems(popularStyles), [popularStyles]);

  const styleName = getStyleName(styleOverview.name);
  const baseMsrp = get(styleOverview, 'price.baseMSRP');
  const currentStyle = popularStyles.find(({ id }) => id === get(styleOverview, 'id'));
  const isDiscontinued = !params.isUsed && isDiscontinuedDate(get(currentStyle, 'styleEndDate'));
  const isMostPopular = !isDiscontinued && get(styleOverview, 'id') === get(popularStyles, '[0].id');

  const hasLabel = isDiscontinued || isMostPopular;
  const labelProps = isDiscontinued && {
    highlightText: (
      <Fragment>
        <sup>*</sup>Discontinued
      </Fragment>
    ),
    highlightTextClasses: 'small fst-italic',
  };

  return (
    <div className={classnames('features-trim-box bg-white px-1 py-1 rounded-12', className)}>
      <div className="label-wrapper mb-1">
        {isDropdownRender && (
          <Label for="fs-style" className="heading-6 text-cool-gray-30 mb-0">
            Select a trim
          </Label>
        )}
      </div>
      <Row className="align-items-center">
        <Col xs={12} md={7} className="dropdown-wrapper mb-1 mb-md-0">
          {isDropdownRender && (
            <div className="style-select-dropdown pos-r">
              <StyledSelect
                id="fs-style"
                value={get(styleOverview, 'id')}
                onChange={onStyleChange}
                name="select-style"
                valueKey="styleId"
                labelKey="name"
                wrapperClassName="w-100"
                overrideClasses={classnames(
                  'style-select d-block w-100 ps-1 pe-2 pb-0_75 bg-white text-truncate size-16 rounded-8',
                  {
                    'mb-0_75 mb-md-0 pt-0_75 pt-md-1_5': !hasLabel,
                    'pt-1_5': hasLabel,
                  }
                )}
                options={options}
                data-tracking-id="fs_model_select"
              />
              <div
                role="presentation"
                className="pos-a text-truncate bg-white"
                style={{
                  pointerEvents: 'none',
                  left: '1rem',
                  right: '2rem',
                  bottom: '0.75rem',
                }}
              >
                <FancyLabel
                  primaryText={styleName.style}
                  secondaryText={styleName.engine}
                  primaryTextClasses="heading-5"
                  secondaryTextClasses="field medium"
                  highlightTextClasses="text-primary-darker size-10 fw-bold"
                  showHighlightText={hasLabel}
                  {...labelProps}
                />
              </div>
            </div>
          )}
        </Col>
        <Col
          xs={12}
          md={5}
          className={classnames('text-center text-md-start', {
            'tax-rebates-container': isElectric,
          })}
        >
          {!!baseMsrp && (
            <Fragment>
              <div className={classnames({ 'mb-0_25 mb-md-1': isElectric })}>
                <div className="small text-cool-gray-40 d-md-inline-block me-md-0_5">Starting MSRP</div>
                <div className="d-inline-block heading-3">{getPriceString(parseInt(baseMsrp, 10))}</div>
              </div>
              {isElectric && (
                <div className="small">
                  <div className="mb-0_25 mb-md-0">
                    <span className="fw-medium">EV Tax Credits & Rebates:</span>{' '}
                    <TaxRebateText taxRebateAmount={taxRebateAmount} />
                  </div>
                  {taxRebateAmount !== null && (
                    <Link
                      to={
                        params.isUsed
                          ? getEvIncentivesUrl({
                              makeName: currentStyle?.makeName,
                              modelName: currentStyle?.modelName,
                              vehCondition: 'USED',
                              incentiveType: 'Tax Credit',
                            })
                          : withTargetText(
                              getDealsUrl(
                                { makeSlug: params.make, modelSlug: params.model, year: params.year },
                                taxRebateAmount
                                  ? { styleId: styleOverview.id, section: INCENTIVE_TYPES.OTHER }
                                  : undefined
                              ),
                              { text: 'federal credit' }
                            )
                      }
                      rel={params.isUsed ? 'nofollow' : undefined}
                      className="text-underline text-primary-darker"
                      data-tracking-id="view_ev_tax_credit"
                    >
                      {taxRebateAmount ? 'Based on income' : 'See other incentives'}
                      <i className="disclaimer ms-0_25 icon-arrow-right4" aria-hidden />
                    </Link>
                  )}
                </div>
              )}
            </Fragment>
          )}
        </Col>
      </Row>
    </div>
  );
}

FeaturesTrimBoxUI.propTypes = {
  params: FeaturesSpecsParams.isRequired,
  styleOverview: FeatureSpecsEntities.FeatureStyleOverviewEntity.isRequired,
  popularStyles: VehicleEntities.PopularStyles.isRequired,
  taxRebateAmount: PropTypes.number,
  className: PropTypes.string,
  onStyleChange: PropTypes.func,
};

FeaturesTrimBoxUI.defaultProps = {
  taxRebateAmount: null,
  className: null,
  onStyleChange: undefined,
};

export const propsAreEqual = (prevProps, newProps) =>
  newProps.taxRebateAmount === null && prevProps?.styleOverview?.id === newProps?.styleOverview?.id;

export const FeaturesTrimBox = connectToModel(React.memo(FeaturesTrimBoxUI, propsAreEqual), {
  taxRebateAmount: bindToPath(
    ({ params, styleOverview }) => getIfEvTaxCreditAmountPath(styleOverview.id, params.isUsed),
    IncentiveModel
  ),
});
